import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Badge } from '../../components/ui/Badge'
import { OrangeSection } from '../../components/ui/OrangeSection'
import PrintButton from '../../components/print/PrintButton'
import { QRGeneratorModal } from '../../components/admin/QRGeneratorModal'
import {
  MapPin,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Package,
  ArrowLeft,
  RefreshCw,
  Building,
  Warehouse,
  Store,
  Home,
  Loader2,
  Search,
  LayoutGrid,
  LayoutList,
  Check,
  Box,
  QrCode
} from 'lucide-react'

interface Location {
  id: string
  name: string
  description?: string
  location_type?: string
  location_code?: string
  qr_code?: string
  parent_id?: string
  parent_name?: string
  item_count: number
  tote_count: number
  sublocation_count: number
  created_at: string
  updated_at: string
}

const API_BASE = '' // Use relative URLs for API calls (proxied by Vite)

export default function LocationsPage() {
  const navigate = useNavigate()

  // Data State
  const [locations, setLocations] = useState<Location[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // UI State
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card')
  const [showQRModal, setShowQRModal] = useState(false)
  const [selectedLocation, setSelectedLocation] = useState<any>(null)

  // Form State
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    location_type: '',
    parent_id: '',
    is_processing: false
  })

  // Fetch all locations
  const fetchLocations = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await fetch(`${API_BASE}/api/locations-pg`)
      const data = await response.json()

      if (data.success) {
        setLocations(data.data)
      } else {
        setError(data.error || 'Failed to fetch locations')
      }
    } catch (err) {
      setError('Network error: Unable to fetch locations')
      console.error('Error fetching locations:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchLocations()
  }, [])

  // Create location
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      setError('Location name is required')
      return
    }

    try {
      const response = await fetch(`${API_BASE}/api/locations-pg`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          location_type: formData.location_type || null,
          parent_id: formData.parent_id || null,
          is_processing: formData.is_processing
        })
      })

      const data = await response.json()

      if (data.success) {
        await fetchLocations()
        setShowCreateForm(false)
        resetForm()
      } else {
        setError(data.error || 'Failed to create location')
      }
    } catch (err) {
      setError('Network error: Unable to create location')
      console.error('Error creating location:', err)
    }
  }

  // Update location
  const handleUpdate = async (id: string) => {
    try {
      const response = await fetch(`${API_BASE}/api/locations-pg/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description || null,
          location_type: formData.location_type || null,
          parent_id: formData.parent_id || null,
          is_processing: formData.is_processing
        })
      })

      const data = await response.json()

      if (data.success) {
        await fetchLocations()
        setEditingId(null)
        resetForm()
      } else {
        setError(data.error || 'Failed to update location')
      }
    } catch (err) {
      setError('Network error: Unable to update location')
      console.error('Error updating location:', err)
    }
  }

  // Delete location
  const handleDelete = async (locationId: string, locationName: string) => {
    if (!confirm(`Are you sure you want to delete "${locationName}"?`)) {
      return
    }

    try {
      const response = await fetch(`${API_BASE}/api/locations-pg/${locationId}`, {
        method: 'DELETE'
      })

      const data = await response.json()

      if (data.success) {
        await fetchLocations()
      } else {
        setError(data.error || 'Failed to delete location')
      }
    } catch (err) {
      setError('Network error: Unable to delete location')
      console.error('Error deleting location:', err)
    }
  }

  // Edit location
  const handleEdit = (location: Location) => {
    setFormData({
      name: location.name,
      description: location.description || '',
      location_type: location.location_type || '',
      parent_id: location.parent_id || '',
      is_processing: location.is_processing || false
    })
    setEditingId(location.id)
    setShowCreateForm(false)
    setError(null)
  }

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      location_type: '',
      parent_id: '',
      is_processing: false
    })
  }

  // Handle QR Code Generation
  const handleGenerateCode = (location: any) => {
    setSelectedLocation(location)
    setShowQRModal(true)
  }

  const handleQRGenerate = async (qrCode: string) => {
    if (!selectedLocation) return

    try {
      const response = await fetch(`${API_BASE}/api/locations-pg/${selectedLocation.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ qr_code: qrCode })
      })

      if (response.ok) {
        fetchLocations()
        setShowQRModal(false)
      }
    } catch (error) {
      console.error('Error saving QR code:', error)
    }
  }

  // Get type icon
  const getTypeIcon = (type?: string) => {
    if (!type) return <MapPin style={{ width: 24, height: 24, color: '#B91BFF' }} />

    const icons: Record<string, any> = {
      warehouse: Warehouse,
      store: Store,
      office: Building,
      storage: Home
    }
    const Icon = icons[type.toLowerCase()] || MapPin
    return <Icon style={{ width: 24, height: 24, color: '#B91BFF' }} />
  }

  // Filter locations
  const filteredLocations = locations.filter(location => {
    const query = searchQuery.toLowerCase()
    return (
      location.name.toLowerCase().includes(query) ||
      location.description?.toLowerCase().includes(query) ||
      location.location_type?.toLowerCase().includes(query) ||
      location.parent_name?.toLowerCase().includes(query)
    )
  })

  // Get parent locations for dropdown (exclude current location when editing)
  const getParentLocationOptions = () => {
    return locations.filter(loc => editingId ? loc.id !== editingId : true)
  }

  return (
    <div style={{
      padding: '24px',
      background: 'linear-gradient(180deg, #1A1F2E 0%, #242938 100%)',
      minHeight: '100vh',
      fontFamily: 'Rajdhani, sans-serif'
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '32px',
        borderBottom: '2px solid #FF6B35',
        paddingBottom: '24px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '16px'
        }}>
          <div>
            <h1 style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '48px',
              fontWeight: 700,
              color: '#FF6B35',
              textTransform: 'uppercase',
              letterSpacing: '2px',
              margin: 0,
              textShadow: '0 0 20px rgba(255, 107, 53, 0.6)'
            }}>
              LOCATIONS MANAGEMENT
            </h1>
            <p style={{
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              color: '#8A95AA',
              textTransform: 'uppercase',
              letterSpacing: '1px',
              margin: '8px 0 0 0'
            }}>
              MANAGE STORAGE LOCATIONS AND WAREHOUSE ORGANIZATION
            </p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => navigate('/admin')}
              style={{
                background: 'rgba(138, 149, 170, 0.1)',
                border: '2px solid rgba(138, 149, 170, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#8A95AA',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(138, 149, 170, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <ArrowLeft style={{ width: 16, height: 16 }} />
              BACK
            </button>
            <button
              onClick={fetchLocations}
              disabled={loading}
              style={{
                background: 'rgba(0, 240, 255, 0.1)',
                border: '2px solid rgba(0, 240, 255, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#00F0FF',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s',
                opacity: loading ? 0.5 : 1
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.background = 'rgba(0, 240, 255, 0.2)'
                  e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <RefreshCw style={{ width: 16, height: 16 }} className={loading ? 'animate-spin' : ''} />
              REFRESH
            </button>
            <button
              onClick={() => setShowCreateForm(true)}
              style={{
                background: 'rgba(107, 207, 127, 0.1)',
                border: '2px solid rgba(107, 207, 127, 0.4)',
                borderRadius: '6px',
                padding: '12px 20px',
                color: '#6BCF7F',
                fontFamily: 'Rajdhani, sans-serif',
                fontSize: '14px',
                fontWeight: 700,
                letterSpacing: '1px',
                textTransform: 'uppercase',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                e.currentTarget.style.boxShadow = 'none'
              }}
            >
              <Plus style={{ width: 16, height: 16 }} />
              NEW LOCATION
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/10 border border-red-500 text-red-500 px-4 py-3 rounded-lg flex items-center justify-between mb-6">
          <span>{error}</span>
          <button onClick={() => setError(null)}>
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Search & View Toggle */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
        <div style={{ position: 'relative', flex: 1 }}>
          <Search style={{
            position: 'absolute',
            left: '12px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: '16px',
            height: '16px',
            color: '#8A95AA'
          }} />
          <input
            type="text"
            placeholder="SEARCH BY NAME, TYPE, OR DESCRIPTION..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{
              width: '100%',
              padding: '12px 12px 12px 40px',
              background: 'rgba(0, 0, 0, 0.4)',
              border: '2px solid rgba(255, 107, 53, 0.3)',
              borderRadius: '6px',
              color: '#FFFFFF',
              fontFamily: 'Rajdhani, sans-serif',
              fontSize: '14px',
              letterSpacing: '1px',
              outline: 'none',
              transition: 'all 0.2s'
            }}
            onFocus={(e) => {
              e.currentTarget.style.border = '2px solid rgba(255, 107, 53, 0.6)'
              e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 107, 53, 0.2)'
            }}
            onBlur={(e) => {
              e.currentTarget.style.border = '2px solid rgba(255, 107, 53, 0.3)'
              e.currentTarget.style.boxShadow = 'none'
            }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            onClick={() => setViewMode('card')}
            style={{
              background: viewMode === 'card' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(138, 149, 170, 0.1)',
              border: viewMode === 'card' ? '2px solid rgba(255, 107, 53, 0.6)' : '2px solid rgba(138, 149, 170, 0.3)',
              borderRadius: '6px',
              padding: '10px',
              color: viewMode === 'card' ? '#FF6B35' : '#8A95AA',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <LayoutGrid style={{ width: 16, height: 16 }} />
          </button>
          <button
            onClick={() => setViewMode('table')}
            style={{
              background: viewMode === 'table' ? 'rgba(255, 107, 53, 0.2)' : 'rgba(138, 149, 170, 0.1)',
              border: viewMode === 'table' ? '2px solid rgba(255, 107, 53, 0.6)' : '2px solid rgba(138, 149, 170, 0.3)',
              borderRadius: '6px',
              padding: '10px',
              color: viewMode === 'table' ? '#FF6B35' : '#8A95AA',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s'
            }}
          >
            <LayoutList style={{ width: 16, height: 16 }} />
          </button>
        </div>
      </div>

      {/* Create Form */}
      {showCreateForm && (
        <Card className="bg-black/60 backdrop-blur-md border-2 border-orange-500/50 shadow-2xl shadow-orange-500/30 mb-6">
          <CardHeader className="border-b border-orange-500/30">
            <CardTitle className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
              Create New Location
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm text-white mb-1 block">Name *</label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Warehouse A"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm text-white mb-1 block">Type</label>
                  <select
                    value={formData.location_type}
                    onChange={(e) => setFormData({ ...formData, location_type: e.target.value })}
                    className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-content"
                  >
                    <option value="">Select Type</option>
                    <option value="warehouse">Warehouse</option>
                    <option value="store">Store</option>
                    <option value="office">Office</option>
                    <option value="storage">Storage</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="text-sm text-white mb-1 block">Parent Location</label>
                <select
                  value={formData.parent_id}
                  onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                  className="w-full px-3 py-2 bg-surface border border-border rounded-lg text-content"
                >
                  <option value="">None (Top Level)</option>
                  {getParentLocationOptions().map(loc => (
                    <option key={loc.id} value={loc.id}>{loc.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm text-white mb-1 block">Description</label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Main storage facility"
                />
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_processing"
                  checked={formData.is_processing}
                  onChange={(e) => setFormData({ ...formData, is_processing: e.target.checked })}
                  className="w-4 h-4"
                />
                <label htmlFor="is_processing" className="text-sm text-white cursor-pointer">
                  Processing Location (show in intake forms)
                </label>
              </div>

              <div className="flex items-center gap-2">
                <Button type="button" onClick={handleCreate}>
                  <Save className="h-4 w-4 mr-2" />
                  Create
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => { setShowCreateForm(false); resetForm(); setError(null); }}
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Locations List */}
      <div className="bg-black/40 backdrop-blur-sm border-2 border-orange-500/30 rounded-xl p-6 shadow-2xl shadow-orange-500/20">
        <div className="space-y-4">
          <div className="flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-400 drop-shadow-[0_0_8px_rgba(251,146,60,0.8)]" />
            <h3 className="text-lg font-medium text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-pink-400">
              Storage Locations ({filteredLocations.length})
            </h3>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
            </div>
          ) : filteredLocations.length === 0 ? (
            <div className="text-center py-12 text-white">
              <MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No locations found. {searchQuery ? 'Try a different search.' : 'Create your first location to get started.'}</p>
            </div>
          ) : viewMode === 'card' ? (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
              gap: '20px'
            }}>
              {filteredLocations.map((location) => (
                <div
                  key={location.id}
                  style={{
                    background: 'linear-gradient(135deg, #1E2530 0%, #252C3A 100%)',
                    border: '2px solid rgba(185, 27, 255, 0.3)',
                    borderRadius: '12px',
                    padding: '20px',
                    transition: 'all 0.3s',
                    position: 'relative'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.border = '2px solid rgba(185, 27, 255, 0.6)'
                    e.currentTarget.style.boxShadow = '0 0 30px rgba(185, 27, 255, 0.2)'
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.border = '2px solid rgba(185, 27, 255, 0.3)'
                    e.currentTarget.style.boxShadow = 'none'
                  }}
                >
                  {/* Header */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '20px',
                    paddingBottom: '16px',
                    borderBottom: '1px solid rgba(185, 27, 255, 0.2)'
                  }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      background: 'rgba(185, 27, 255, 0.1)',
                      border: '2px solid rgba(185, 27, 255, 0.3)',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {getTypeIcon(location.location_type)}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h3 style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '20px',
                        fontWeight: 700,
                        color: '#B91BFF',
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        margin: 0,
                        textShadow: '0 0 10px rgba(185, 27, 255, 0.3)'
                      }}>
                        {location.name}
                      </h3>
                      {location.description && (
                        <p style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '12px',
                          color: '#8A95AA',
                          margin: '4px 0 0 0'
                        }}>
                          {location.description}
                        </p>
                      )}
                    </div>
                    {location.location_type && (
                      <div style={{
                        padding: '4px 12px',
                        background: 'rgba(185, 27, 255, 0.2)',
                        border: '1px solid rgba(185, 27, 255, 0.4)',
                        borderRadius: '4px',
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '11px',
                        fontWeight: 700,
                        color: '#B91BFF',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                      }}>
                        {location.location_type}
                      </div>
                    )}
                  </div>

                  {/* Stats Grid */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, 1fr)',
                    gap: '12px',
                    marginBottom: '16px'
                  }}>
                    {location.parent_name && (
                      <div style={{
                        gridColumn: '1 / -1',
                        background: 'rgba(0, 240, 255, 0.1)',
                        border: '1px solid rgba(0, 240, 255, 0.3)',
                        borderRadius: '6px',
                        padding: '8px'
                      }}>
                        <div style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '10px',
                          color: '#8A95AA',
                          marginBottom: '4px',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>PARENT LOCATION</div>
                        <div style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '14px',
                          fontWeight: 700,
                          color: '#00F0FF'
                        }}>{location.parent_name}</div>
                      </div>
                    )}

                    <div style={{
                      background: 'rgba(255, 215, 61, 0.1)',
                      border: '1px solid rgba(255, 215, 61, 0.3)',
                      borderRadius: '6px',
                      padding: '8px'
                    }}>
                      <div style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '10px',
                        color: '#8A95AA',
                        marginBottom: '4px',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>ITEMS</div>
                      <div style={{
                        fontFamily: 'Rajdhani, monospace',
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#FFD93D'
                      }}>{location.item_count}</div>
                    </div>

                    <div style={{
                      background: 'rgba(107, 207, 127, 0.1)',
                      border: '1px solid rgba(107, 207, 127, 0.3)',
                      borderRadius: '6px',
                      padding: '8px'
                    }}>
                      <div style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '10px',
                        color: '#8A95AA',
                        marginBottom: '4px',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>TOTES</div>
                      <div style={{
                        fontFamily: 'Rajdhani, monospace',
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#6BCF7F'
                      }}>{location.tote_count}</div>
                    </div>

                    <div style={{
                      gridColumn: '1 / -1',
                      background: 'rgba(255, 107, 53, 0.1)',
                      border: '1px solid rgba(255, 107, 53, 0.3)',
                      borderRadius: '6px',
                      padding: '8px'
                    }}>
                      <div style={{
                        fontFamily: 'Rajdhani, sans-serif',
                        fontSize: '10px',
                        color: '#8A95AA',
                        marginBottom: '4px',
                        letterSpacing: '1px',
                        textTransform: 'uppercase'
                      }}>SUBLOCATIONS</div>
                      <div style={{
                        fontFamily: 'Rajdhani, monospace',
                        fontSize: '18px',
                        fontWeight: 700,
                        color: '#FF6B35'
                      }}>{location.sublocation_count}</div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  {editingId === location.id ? (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '12px',
                      paddingTop: '16px',
                      borderTop: '1px solid rgba(185, 27, 255, 0.2)'
                    }}>
                      <div>
                        <label style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '11px',
                          color: '#8A95AA',
                          marginBottom: '4px',
                          display: 'block',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>NAME *</label>
                        <input
                          type="text"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          placeholder="Name *"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'rgba(0, 0, 0, 0.4)',
                            border: '1px solid rgba(138, 149, 170, 0.3)',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '11px',
                          color: '#8A95AA',
                          marginBottom: '4px',
                          display: 'block',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>DESCRIPTION</label>
                        <input
                          type="text"
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          placeholder="Description"
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'rgba(0, 0, 0, 0.4)',
                            border: '1px solid rgba(138, 149, 170, 0.3)',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        />
                      </div>
                      <div>
                        <label style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '11px',
                          color: '#8A95AA',
                          marginBottom: '4px',
                          display: 'block',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>TYPE</label>
                        <select
                          value={formData.location_type}
                          onChange={(e) => setFormData({ ...formData, location_type: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'rgba(0, 0, 0, 0.4)',
                            border: '1px solid rgba(138, 149, 170, 0.3)',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        >
                          <option value="">Select Type</option>
                          <option value="warehouse">Warehouse</option>
                          <option value="store">Store</option>
                          <option value="office">Office</option>
                          <option value="storage">Storage</option>
                        </select>
                      </div>
                      <div>
                        <label style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '11px',
                          color: '#8A95AA',
                          marginBottom: '4px',
                          display: 'block',
                          letterSpacing: '1px',
                          textTransform: 'uppercase'
                        }}>PARENT LOCATION</label>
                        <select
                          value={formData.parent_id}
                          onChange={(e) => setFormData({ ...formData, parent_id: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: 'rgba(0, 0, 0, 0.4)',
                            border: '1px solid rgba(138, 149, 170, 0.3)',
                            borderRadius: '6px',
                            color: '#FFFFFF',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                        >
                          <option value="">None (Top Level)</option>
                          {getParentLocationOptions().map(loc => (
                            <option key={loc.id} value={loc.id}>{loc.name}</option>
                          ))}
                        </select>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                        <input
                          type="checkbox"
                          id={`is_processing_${location.id}`}
                          checked={formData.is_processing}
                          onChange={(e) => setFormData({ ...formData, is_processing: e.target.checked })}
                          style={{ width: '16px', height: '16px' }}
                        />
                        <label htmlFor={`is_processing_${location.id}`} style={{
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '11px',
                          color: '#8A95AA',
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          cursor: 'pointer'
                        }}>PROCESSING LOCATION</label>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => handleUpdate(location.id)}
                          style={{
                            flex: 1,
                            background: 'rgba(107, 207, 127, 0.1)',
                            border: '2px solid rgba(107, 207, 127, 0.4)',
                            borderRadius: '6px',
                            padding: '10px',
                            color: '#6BCF7F',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '12px',
                            fontWeight: 700,
                            letterSpacing: '1px',
                            textTransform: 'uppercase',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(107, 207, 127, 0.2)'
                            e.currentTarget.style.boxShadow = '0 0 15px rgba(107, 207, 127, 0.3)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(107, 207, 127, 0.1)'
                            e.currentTarget.style.boxShadow = 'none'
                          }}
                        >
                          <Save style={{ width: 14, height: 14 }} />
                          Save
                        </button>
                        <button
                          onClick={() => { setEditingId(null); resetForm(); }}
                          style={{
                            background: 'rgba(138, 149, 170, 0.1)',
                            border: '2px solid rgba(138, 149, 170, 0.4)',
                            borderRadius: '6px',
                            padding: '10px',
                            color: '#8A95AA',
                            fontFamily: 'Rajdhani, sans-serif',
                            fontSize: '12px',
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.2s'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(138, 149, 170, 0.2)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(138, 149, 170, 0.1)'
                          }}
                        >
                          <X style={{ width: 14, height: 14 }} />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', gap: '8px', paddingTop: '16px', borderTop: '1px solid rgba(185, 27, 255, 0.2)' }}>
                      <button
                        onClick={() => handleEdit(location)}
                        style={{
                          flex: 1,
                          background: 'rgba(255, 215, 61, 0.1)',
                          border: '2px solid rgba(255, 215, 61, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#FFD93D',
                          fontFamily: 'Rajdhani, sans-serif',
                          fontSize: '12px',
                          fontWeight: 700,
                          letterSpacing: '1px',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 61, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(255, 215, 61, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(255, 215, 61, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <Edit style={{ width: 14, height: 14 }} />
                        Edit
                      </button>
                      <PrintButton entityType="location" entityData={location} variant="icon" />
                      <button
                        onClick={() => handleGenerateCode(location)}
                        style={{
                          padding: '8px',
                          background: 'transparent',
                          border: '2px solid #00F0FF',
                          borderRadius: '6px',
                          color: '#00F0FF',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        title="Generate QR Code"
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(0, 240, 255, 0.1)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 240, 255, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'transparent'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <QrCode size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(location.id, location.name)}
                        style={{
                          background: 'rgba(239, 68, 68, 0.1)',
                          border: '2px solid rgba(239, 68, 68, 0.4)',
                          borderRadius: '6px',
                          padding: '10px',
                          color: '#EF4444',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(239, 68, 68, 0.3)'
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'
                          e.currentTarget.style.boxShadow = 'none'
                        }}
                      >
                        <Trash2 style={{ width: 14, height: 14 }} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-black/60 backdrop-blur-md border border-orange-500/30 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-orange-900/30 border-b border-orange-500/30">
                  <tr>
                    <th className="text-left p-3 text-orange-200">Name</th>
                    <th className="text-left p-3 text-orange-200">Type</th>
                    <th className="text-left p-3 text-orange-200">Parent</th>
                    <th className="text-left p-3 text-orange-200">Items</th>
                    <th className="text-left p-3 text-orange-200">Totes</th>
                    <th className="text-left p-3 text-orange-200">Sublocations</th>
                    <th className="text-left p-3 text-orange-200">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredLocations.map(location => (
                    <tr key={location.id} className="border-b border-orange-500/10 hover:bg-orange-500/10">
                      <td className="p-3 text-orange-100">{location.name}</td>
                      <td className="p-3 text-orange-300 capitalize">{location.location_type || '-'}</td>
                      <td className="p-3 text-orange-300">{location.parent_name || '-'}</td>
                      <td className="p-3 text-orange-300">{location.item_count}</td>
                      <td className="p-3 text-orange-300">{location.tote_count}</td>
                      <td className="p-3 text-orange-300">{location.sublocation_count}</td>
                      <td className="p-3">
                        <div className="flex gap-1">
                          <Button size="sm" variant="ghost" onClick={() => handleEdit(location)}>
                            <Edit className="h-4 w-4" />
                          </Button>
                          <PrintButton entityType="location" entityData={location} variant="icon" />
                          <Button size="sm" variant="ghost" onClick={() => handleGenerateCode(location)}>
                            <QrCode className="h-4 w-4" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(location.id, location.name)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* QR Generator Modal */}
      {showQRModal && selectedLocation && (
        <QRGeneratorModal
          isOpen={showQRModal}
          onClose={() => setShowQRModal(false)}
          onGenerate={handleQRGenerate}
          type="LOCATION"
          id={selectedLocation.id}
          code={selectedLocation.location_code}
          title={`Generate QR Code - ${selectedLocation.name}`}
        />
      )}
    </div>
  )
}
